import { Alert, Button, Card, List, PasswordInput, Stack, Text, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zod4Resolver as zodResolver } from 'mantine-form-zod-resolver'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { IconAlertCircle, IconLock } from '@tabler/icons-react'
import { PASSWORD_MIN, cambioPassword, type CambioPassword } from '@serkat/shared'
import { ErrorApi, api } from '../../api/cliente'

/**
 * Pantalla obligatoria del primer acceso. No hay forma de saltarla: mientras la
 * contraseña siga sin cambiar, el backend rechaza todo lo demás con 403.
 */
export function CambiarPasswordPagina() {
  const qc = useQueryClient()

  const form = useForm<CambioPassword>({
    initialValues: { actual: '', nueva: '' },
    validate: zodResolver(cambioPassword),
  })

  const cambiar = useMutation({
    mutationFn: (v: CambioPassword) => api.post('/auth/cambiar-password', v),
    onSuccess: () => {
      // El servidor revocó TODAS las sesiones y borró la cookie. Vaciar la
      // caché no basta: no vuelve a consultar y la pantalla queda colgada
      // mostrando el formulario aunque el cambio ya se aplicó. Una recarga
      // completa deja el estado limpio y lleva al inicio de sesión, que es
      // exactamente lo que corresponde.
      qc.clear()
      window.location.replace('/')
    },
  })

  const mensajeError =
    cambiar.error instanceof ErrorApi ? cambiar.error.message : cambiar.error ? 'No se pudo conectar' : null

  return (
    <Stack align="center" justify="center" mih="100vh" bg="var(--mantine-color-body)" p="md">
      <Card shadow="sm" padding="xl" radius="md" withBorder w={440}>
        <Stack gap="md">
          <div>
            <Title order={3}>Cambie su contraseña</Title>
            <Text size="sm" c="dimmed">
              Es su primer acceso. Hasta cambiarla, el sistema no permite otras acciones.
            </Text>
          </div>

          <Alert color="blue" variant="light" icon={<IconLock size={16} />}>
            <Text size="sm">
              Todas las cuentas se crean con la misma contraseña. Si no la cambia,
              cualquiera que la conozca podría emitir o aprobar documentos en su nombre.
            </Text>
          </Alert>

          {mensajeError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {mensajeError}
            </Alert>
          )}

          <form onSubmit={form.onSubmit((v) => cambiar.mutate(v))}>
            <Stack gap="sm">
              <PasswordInput
                label="Contraseña actual"
                autoComplete="current-password"
                {...form.getInputProps('actual')}
              />
              <PasswordInput
                label="Contraseña nueva"
                autoComplete="new-password"
                {...form.getInputProps('nueva')}
              />
              <List size="xs" c="dimmed" spacing={2}>
                <List.Item>Al menos {PASSWORD_MIN} caracteres</List.Item>
                <List.Item>Debe combinar letras y números</List.Item>
                <List.Item>Distinta de la actual</List.Item>
              </List>
              <Button type="submit" loading={cambiar.isPending} fullWidth mt="xs">
                Cambiar y volver a entrar
              </Button>
            </Stack>
          </form>
        </Stack>
      </Card>
    </Stack>
  )
}
